import React, { memo, useState, useRef, useEffect, useCallback, useMemo, FC } from 'react';
import clsx from 'clsx';
import { Heading, Types, Button, Icon, RichText } from '@marriott/mi-ui-library';
import { EditableComponent } from '@adobe/aem-react-editable-components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useSession } from '@marriott/mi-headless-utils';
import { StickyCardSection } from '../../molecules/StickyCardSection';
import { CardDetailsContent } from '../../molecules/CardDetailsContent';
import { CardCompareCheckbox } from '../../molecules/CardCompareCheckbox';
import { CobrandImage } from '../../molecules/CobrandImage/CobrandImage';
import { CardPointsList } from '../../molecules/CardPointsList';
import { CardDetailsProps, MobileAccordionProps, CTASectionProps, CardLeftSectionProps } from './CardDetails.types';
import { StyledCardDetails } from './CardDetails.styles';
import { referAFriend, replaceWhitespaceAndAddBtn } from '../../modules/utils';
import { CardAwardBadge } from '../../molecules/CardAwardBadge';
import { LinkType, LinkTarget, StringBoolean, ARIA_LABELS } from '../../modules/constants';

export const CardDetailsConfig = {
  emptyLabel: 'Card Details',
  isEmpty: (): boolean => true,
  resourceType: `/components/content/carddetails`,
} as const;

/**
 * CardDetailsComp - Main card details component showing card information and CTA
 *
 * Performance optimizations:
 * - Memoized component with React.memo
 * - useMemo for computed values
 * - useCallback for event handlers
 * - Lazy loading of heavy components
 *
 * @param props - CardDetailsProps
 */
export const CardDetailsComp: FC<CardDetailsProps> = memo<CardDetailsProps>(props => {
  const {
    header,
    cardSummary,
    cardType,
    expandAccordionForMobile,
    styleclass,
    componentId,
    linkUrl,
    openInaNewTab,
    linkText,
    accordionHeader = 'Overview',
    cardCode,
    trackingProperties,
    cardHeader,
    imagePath,
    imagePathWithoutBadge,
    altText,
    isComparisonTrayEnabled,
    layoutType = 'default',
    cardDescription,
    pricingTermsGuest,
    pricingTermsSignedIn,
    pointsHeader,
    pointsList,
    linksSection,
    awardImagePath,
    awardAltText,
    awardUrl,
    awardOpeninNewTab,
  } = props;

  // Refs for DOM manipulation
  const cardDetailsRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  // State management
  const [showMobileAccordion, setShowMobileAccordion] = useState<boolean>(!expandAccordionForMobile);

  // Session data
  const { datalayer } = useSession();

  // Memoized computed values - prevents unnecessary recalculations
  const linkType = useMemo<LinkType>(
    () => (openInaNewTab === StringBoolean.TRUE ? LinkType.EXTERNAL : LinkType.INTERNAL),
    [openInaNewTab]
  );

  const buttonId = useMemo<string>(() => replaceWhitespaceAndAddBtn(componentId), [componentId]);

  const displayImagePath = useMemo<string>(
    () => imagePathWithoutBadge || imagePath,
    [imagePathWithoutBadge, imagePath]
  );

  const shouldShowComparisonCheckbox = useMemo<boolean>(
    () => Boolean(isComparisonTrayEnabled && cardHeader),
    [isComparisonTrayEnabled, cardHeader]
  );

  // Event handlers - memoized to prevent recreation on each render
  const toggleAccordion = useCallback((): void => {
    setShowMobileAccordion(prev => !prev);
  }, []);

  // Initialize refer-a-friend functionality
  useEffect(() => {
    if (datalayer && cardDetailsRef.current) {
      referAFriend(datalayer, cardDetailsRef);
    }
  }, [datalayer]);

  // V2 layoutType - simplified two-section design using existing fields
  if (layoutType === 'enhanced') {
    // Use session data to determine which pricing to show
    const pricingContent = pricingTermsSignedIn || pricingTermsGuest;

    return (
      <StyledCardDetails
        data-component-name="o-cobrand-CardDetails"
        data-testid="cobrand-CardDetails"
        className={clsx('card-details', 'card-details--v2', styleclass)}
        data-cardtype={cardType}
        id={componentId}
        ref={cardDetailsRef}
      >
        <div className="card-details-v2-wrapper">
          {/* Grey Section: Card Info */}
          <div className="card-details-v2-card-info">
            {/* Card Image */}
            <CobrandImage defaultImageURL={imagePath} altText={altText} customClass="sticky-card-image" />

            {/* Card Title */}
            <Heading
              titleText={header}
              variation={Types.headingType.subtitle}
              fontSize={Types.size.extraLarge}
              customClass="text-center mt-4"
            />

            {/* Annual Fee / Pricing Terms */}
            {pricingContent && (
              <div className="card-details-annual-fee">
                <RichText text={pricingContent} componentId={`annual-fee-${componentId}`} />
              </div>
            )}

            {/* CTA Button */}
            <Button
              id={buttonId}
              isLink
              href={linkUrl}
              trackingProperties={trackingProperties}
              target={openInaNewTab === StringBoolean.TRUE ? LinkTarget.BLANK : LinkTarget.SELF}
              className="m-button-m py-2 mb-2 mt-3"
              linkType={linkType}
              ref={buttonRef}
              buttonCopy={linkText}
              linkAriaLabelOpenNewWindow={openInaNewTab === StringBoolean.TRUE ? ARIA_LABELS.OPEN_NEW_TAB : ''}
            />

            {/* Compare Checkbox */}
            {shouldShowComparisonCheckbox && (
              <CardCompareCheckbox
                cardCode={cardCode}
                cardType={cardType}
                imagePath={displayImagePath}
                cardHeader={cardHeader}
                componentId={buttonId}
                customClass="mt-4"
              />
            )}

            {/* NerdWallet Image */}
            {awardImagePath && awardAltText && (
              <CardAwardBadge
                awardImagePath={awardImagePath}
                awardAltText={awardAltText}
                awardUrl={awardUrl}
                awardOpeninNewTab={awardOpeninNewTab}
              />
            )}
          </div>

          {/* White Section: Featured Offer & Points */}
          <div className="card-details-v2-card-details">
            {/* Featured Offer - uses cardSummary */}
            {cardSummary && (
              <RichText
                text={cardSummary}
                componentId={`featured-offer-${componentId}`}
                customClass="featured-offer-highlight"
              />
            )}

            {/* Points List - earning structure */}
            <CardPointsList
              pointsHeader={pointsHeader}
              pointsList={pointsList}
              cardType={cardType}
              componentId={`${componentId}-points`}
              layoutType={layoutType}
            />

            {/* Additional Benefits Accordion - uses cardDescription */}
            {cardDescription && (
              <div className="accordion m-accordion-container card-details-v2-accordion">
                <Button
                  className="accordion-button py-3"
                  ariaExpanded={!showMobileAccordion}
                  callback={toggleAccordion}
                >
                  <div className="m-accordion-title">
                    {accordionHeader}
                    <Icon
                      iconClass={clsx('accordion-arrow-icon', {
                        'icon-arrow-up': showMobileAccordion,
                        'icon-arrow-down': !showMobileAccordion,
                      })}
                    />
                  </div>
                </Button>
                <div className={clsx('accordion-collapse', { 'collapse d-none': showMobileAccordion })}>
                  <RichText text={cardDescription} componentId={`additional-benefits-${componentId}`} />
                </div>
                {linksSection && (
                  <div>
                    <RichText text={linksSection} componentId={`links-section`} customClass="links-section" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </StyledCardDetails>
    );
  }

  // V1 layoutType (existing implementation)

  return (
    <StyledCardDetails
      data-component-name="o-cobrand-CardDetails"
      data-testid="cobrand-CardDetails"
      className={clsx('card-details mt-2 mt-md-5 pt-3 mb-2 mb-md-5 pb-3', styleclass)}
      data-cardtype={cardType}
      id={componentId}
      ref={cardDetailsRef}
    >
      <div className="pt-md-0 card-detail--border t-background-color">
        <div className="card-detail-wrapper pt-lg-4 mt-5 px-lg-4 px-3 pb-5">
          {/* Desktop Header - Hidden on mobile */}
          <DesktopHeader header={header} />

          <div className="d-flex flex-column flex-md-row">
            {/* Left Section - Card Image, Summary, CTA */}
            <CardLeftSection
              props={props}
              cardSummary={cardSummary}
              header={header}
              showMobileAccordion={showMobileAccordion}
              toggleAccordion={toggleAccordion}
              accordionHeader={accordionHeader}
              buttonId={buttonId}
              linkUrl={linkUrl}
              trackingProperties={trackingProperties}
              openInaNewTab={openInaNewTab}
              linkType={linkType}
              buttonRef={buttonRef}
              linkText={linkText}
              shouldShowComparisonCheckbox={shouldShowComparisonCheckbox}
              cardCode={cardCode}
              cardType={cardType}
              displayImagePath={displayImagePath}
              cardHeader={cardHeader}
              awardAltText={awardAltText}
              awardImagePath={awardImagePath}
              awardUrl={awardUrl}
              awardOpeninNewTab={awardOpeninNewTab}
            />

            {/* Right Section - Points and Benefits (Desktop) */}
            <CardDetailsContent {...props} componentId={`${componentId}-content`} showAccordion={false} />
          </div>
        </div>
      </div>
    </StyledCardDetails>
  );
});

CardDetailsComp.displayName = 'CardDetailsComp';

/**
 * DesktopHeader - Separated component for desktop header
 * Hidden on mobile devices
 */
const DesktopHeader: FC<{ header: string }> = memo(({ header }) => (
  <div className="mr-lg-5">
    <Heading
      titleText={header}
      variation={Types.headingType.title}
      fontSize={Types.size.medium}
      customClass="text-md-left mb-4 mb-lg-5 pb-lg-2 ml-md-3 pl-xl-4 pl-lg-5 col-md-10 col-lg-12 d-none d-md-block"
    />
  </div>
));

DesktopHeader.displayName = 'DesktopHeader';

/**
 * MobileAccordion - Accordion component for mobile view
 * Shows points and benefits in collapsible section
 */
const MobileAccordion: FC<MobileAccordionProps> = memo(
  ({ showMobileAccordion, toggleAccordion, accordionHeader, props }) => (
    <div className="accordion m-accordion-container d-block d-md-none px-0">
      <Button className="accordion-button py-3" ariaExpanded={!showMobileAccordion} callback={toggleAccordion}>
        <div className="m-accordion-title">
          {accordionHeader}
          <Icon
            iconClass={clsx('accordion-arrow-icon', {
              'icon-arrow-up': showMobileAccordion,
              'icon-arrow-down': !showMobileAccordion,
            })}
          />
        </div>
      </Button>
      <div className={clsx('accordion-collapse', { 'collapse d-none': showMobileAccordion })}>
        <CardDetailsContent {...props} showAccordion={true} />
      </div>
    </div>
  )
);

MobileAccordion.displayName = 'MobileAccordion';

/**
 * CTASection - Call-to-action button and comparison checkbox
 */
const CTASection: FC<CTASectionProps> = memo(
  ({
    buttonId,
    linkUrl,
    trackingProperties,
    openInaNewTab,
    linkType,
    buttonRef,
    linkText,
    shouldShowComparisonCheckbox,
    cardCode,
    cardType,
    displayImagePath,
    cardHeader,
  }) => (
    <div className="d-flex justify-content-center flex-column align-items-center">
      <Button
        id={buttonId}
        isLink
        href={linkUrl}
        trackingProperties={trackingProperties}
        target={openInaNewTab === StringBoolean.TRUE ? LinkTarget.BLANK : LinkTarget.SELF}
        className="m-button-m py-2 mb-1 mr-0"
        linkType={linkType}
        ref={buttonRef}
        buttonCopy={linkText}
        linkAriaLabelOpenNewWindow={openInaNewTab === StringBoolean.TRUE ? ARIA_LABELS.OPEN_NEW_TAB : ''}
      />

      {shouldShowComparisonCheckbox && buttonId && (
        <CardCompareCheckbox
          cardCode={cardCode}
          cardType={cardType}
          imagePath={displayImagePath}
          cardHeader={cardHeader}
          componentId={buttonId}
          customClass={'mt-4'}
        />
      )}
    </div>
  )
);

CTASection.displayName = 'CTASection';

/**
 * CardLeftSection - Left column containing card image, summary, and CTA
 */
const CardLeftSection: FC<CardLeftSectionProps> = memo(
  ({
    props,
    cardSummary,
    header,
    showMobileAccordion,
    toggleAccordion,
    accordionHeader,
    buttonId,
    linkUrl,
    trackingProperties,
    openInaNewTab,
    linkType,
    buttonRef,
    linkText,
    shouldShowComparisonCheckbox,
    cardCode,
    cardType,
    displayImagePath,
    cardHeader,
    awardImagePath,
    awardAltText,
    awardUrl,
    awardOpeninNewTab,
  }) => (
    <div className="col-xl-3 col-lg-4 col-md-4 mx-auto col-12 d-flex justify-content-center">
      <div className="d-flex flex-column">
        {/* Card Image and Summary */}
        <StickyCardSection
          {...props}
          variant="vertical"
          hideButtonOnMobile={true}
          cardTitle={cardSummary}
          header={header}
        />

        {/* NerdWallet Image - Mobile Only */}
        {awardImagePath && awardAltText && (
          <CardAwardBadge
            awardImagePath={awardImagePath}
            awardAltText={awardAltText}
            awardUrl={awardUrl}
            awardOpeninNewTab={awardOpeninNewTab}
            customClass="d-block d-md-none mt-3"
          />
        )}

        {/* Mobile Accordion for Points/Benefits */}
        <MobileAccordion
          showMobileAccordion={showMobileAccordion}
          toggleAccordion={toggleAccordion}
          accordionHeader={accordionHeader}
          props={props}
        />

        {/* CTA and Comparison Checkbox */}
        <CTASection
          buttonId={buttonId}
          linkUrl={linkUrl}
          trackingProperties={trackingProperties}
          openInaNewTab={openInaNewTab}
          linkType={linkType}
          buttonRef={buttonRef}
          linkText={linkText}
          shouldShowComparisonCheckbox={shouldShowComparisonCheckbox}
          cardCode={cardCode}
          cardType={cardType}
          displayImagePath={displayImagePath}
          cardHeader={cardHeader}
        />

        {/* NerdWallet Image - Desktop Only */}
        {awardImagePath && awardAltText && (
          <CardAwardBadge
            awardImagePath={awardImagePath}
            awardAltText={awardAltText}
            awardUrl={awardUrl}
            awardOpeninNewTab={awardOpeninNewTab}
            customClass="d-none d-md-block"
          />
        )}
      </div>
    </div>
  )
);

CardLeftSection.displayName = 'CardLeftSection';

/**
 * CardDetails - AEM Editable wrapper component
 */
export const CardDetails: FC<CardDetailsProps> = props => (
  <EditableComponent config={CardDetailsConfig} {...props}>
    <CardDetailsComp {...props} />
  </EditableComponent>
);
